import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";

import { api, formatApiError, DAILY_MINUTES, INTENSITY_WORDS, MODALITIES, CORRECTIONS, INTEREST_SUGGESTIONS, PROGRAM_LENGTHS, GOALS, LEVELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LanguagePicker, { QualityBadge } from "@/components/LanguagePicker";

const STEP_KEYS = [
    "prenom",
    "langue_principale",
    "langue_cible",
    "variante",
    "niveau",
    "objectif",
    "temps",
    "intensite",
    "interets",
    "modalite",
    "correction",
    "duree",
];

export default function Onboarding() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState({
        first_name: user?.first_name || "",
        native_language: "fr",
        target_language: "",
        variant: null,
        level: "inconnu",
        goal: "conversation",
        goal_detail: "",
        daily_minutes: 20,
        intensity_words: 25,
        interests: [],
        modality: "multimodal",
        correction: "standard",
        program_length_days: 30,
        completed_steps: [],
    });
    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [generating, setGenerating] = useState(false);

    // Load languages once so we can compute variants for the target language.
    useEffect(() => {
        api.get("/languages").then((r) => setLanguages(r.data.languages || []));
    }, []);

    // Resume from server state, if any.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/profile/me");
                if (data.profile) {
                    setProfile((p) => ({ ...p, ...data.profile }));
                    // Advance to the next uncompleted step
                    const done = new Set(data.profile.completed_steps || []);
                    let next = 0;
                    for (let i = 0; i < STEP_KEYS.length; i++) {
                        if (!done.has(STEP_KEYS[i])) { next = i; break; }
                        next = i + 1;
                    }
                    setStep(Math.min(next, STEP_KEYS.length - 1));
                }
            } catch (err) {
                // Not fatal — a new user without any saved profile is expected
                // to 404 here. Log so a real 500 stands out during triage.
                if (err?.response?.status && err.response.status >= 500) {
                    console.warn("[Onboarding] /profile/me resume failed:", err);
                }
            }
        })();
    }, []);

    const targetLang = useMemo(
        () => languages.find((l) => l.iso === profile.target_language) || null,
        [languages, profile.target_language],
    );

    // Sibling variants (e.g. en-GB + en-US) for the chosen target language.
    const variantList = useMemo(() => {
        if (!targetLang) return [];
        const base = targetLang.base || targetLang.iso;
        return languages.filter((l) => (l.base || l.iso) === base && l.variant);
    }, [targetLang, languages]);

    const totalSteps = STEP_KEYS.length;
    const progress = ((step + 1) / totalSteps) * 100;

    async function persist(overrides = {}, alsoComplete = null) {
        const next = { ...profile, ...overrides };
        if (alsoComplete && !next.completed_steps.includes(alsoComplete)) {
            next.completed_steps = [...next.completed_steps, alsoComplete];
        }
        setProfile(next);
        try {
            await api.post("/profile/save", next);
        } catch (err) {
            toast.error(formatApiError(err));
        }
        return next;
    }

    async function goNext(overrides = {}) {
        setSaving(true);
        await persist(overrides, STEP_KEYS[step]);
        setSaving(false);
        setStep((s) => Math.min(s + 1, totalSteps - 1));
    }
    function goBack() {
        setStep((s) => Math.max(s - 1, 0));
    }

    async function generateProgram() {
        setGenerating(true);
        try {
            await api.post("/program/generate", { length_days: profile.program_length_days });
            await refreshUser();
            toast.success("Votre programme est prêt.");
            navigate("/dashboard", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
            setGenerating(false);
        }
    }

    return (
        <div className="min-h-screen bg-paper" data-testid="onboarding-page">
            <header className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex justify-between items-center">
                <span className="text-xl font-serif tracking-tight">
                    Lexia School
                </span>
                <span className="text-xs text-inkSoft">
                    Étape <span data-testid="step-index">{step + 1}</span> / {totalSteps}
                </span>
            </header>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                <div className="h-1 bg-lexiBorder rounded-full overflow-hidden mb-10">
                    <div
                        className="h-full bg-terracotta transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                        data-testid="progress-bar"
                    />
                </div>

                <div className="bg-white border border-lexiBorder rounded-2xl p-8 md:p-10 animate-fade-up" data-testid={`step-${STEP_KEYS[step]}`}>
                    {step === 0 && (
                        <StepPrenom
                            value={profile.first_name}
                            onNext={(v) => goNext({ first_name: v })}
                        />
                    )}
                    {step === 1 && (
                        <StepLangueSelector
                            title="Votre langue principale"
                            hint="La langue dans laquelle vous êtes à l'aise (français par défaut)."
                            value={profile.native_language}
                            onChange={(iso) => setProfile((p) => ({ ...p, native_language: iso }))}
                            onNext={() => goNext({ native_language: profile.native_language })}
                            onBack={goBack}
                            testId="native"
                        />
                    )}
                    {step === 2 && (
                        <StepLangueSelector
                            title="Quelle langue voulez-vous apprendre ?"
                            hint="Vous pourrez en changer plus tard. Choisissez celle qui vous motive maintenant."
                            value={profile.target_language}
                            onChange={(iso) => {
                                // If picking a variant, remember it; otherwise clear.
                                const l = languages.find((x) => x.iso === iso);
                                setProfile((p) => ({
                                    ...p,
                                    target_language: iso,
                                    variant: l?.variant || null,
                                }));
                            }}
                            excludeIso={profile.native_language}
                            onNext={() => {
                                if (!profile.target_language) {
                                    toast.error("Choisissez une langue cible.");
                                    return;
                                }
                                // If the picked language has siblings (variants), go to step 3;
                                // otherwise skip that step.
                                const base = targetLang?.base || targetLang?.iso;
                                const siblings = languages.filter((l) => (l.base || l.iso) === base && l.variant);
                                if (siblings.length > 1) {
                                    goNext({
                                        target_language: profile.target_language,
                                        variant: profile.variant,
                                    });
                                } else {
                                    // mark both variant + langue_cible steps as done
                                    persist(
                                        {
                                            target_language: profile.target_language,
                                            variant: null,
                                        },
                                        "langue_cible",
                                    ).then(() => {
                                        setProfile((p) => ({
                                            ...p,
                                            completed_steps: [
                                                ...new Set([
                                                    ...p.completed_steps,
                                                    "langue_cible",
                                                    "variante",
                                                ]),
                                            ],
                                        }));
                                        setStep(4);
                                    });
                                }
                            }}
                            onBack={goBack}
                            testId="target"
                        />
                    )}
                    {step === 3 && (
                        <StepVariant
                            targetLang={targetLang}
                            variants={variantList}
                            value={profile.target_language}
                            onChange={(l) =>
                                setProfile((p) => ({
                                    ...p,
                                    target_language: l.iso,
                                    variant: l.variant || null,
                                }))
                            }
                            onNext={() => goNext({
                                target_language: profile.target_language,
                                variant: profile.variant,
                            })}
                            onBack={goBack}
                        />
                    )}
                    {step === 4 && (
                        <StepNiveau
                            value={profile.level}
                            onChange={(v) => setProfile((p) => ({ ...p, level: v }))}
                            onNext={() => goNext({ level: profile.level })}
                            onBack={goBack}
                            onTakeTest={async () => {
                                setSaving(true);
                                await persist({ level: "inconnu" }, "niveau");
                                setSaving(false);
                                try {
                                    const { data } = await api.post("/placement/start", {
                                        target_language: profile.target_language,
                                        variant: profile.variant,
                                    });
                                    navigate(`/placement/${data.test_id}`);
                                } catch (err) {
                                    toast.error(formatApiError(err));
                                }
                            }}
                            targetLang={targetLang}
                        />
                    )}
                    {step === 5 && (
                        <StepObjectif
                            goal={profile.goal}
                            goalDetail={profile.goal_detail}
                            setGoal={(g) => setProfile((p) => ({ ...p, goal: g }))}
                            setDetail={(d) => setProfile((p) => ({ ...p, goal_detail: d }))}
                            onNext={() => goNext({ goal: profile.goal, goal_detail: profile.goal_detail })}
                            onBack={goBack}
                        />
                    )}
                    {step === 6 && (
                        <StepChoicePills
                            title="Combien de temps par jour ?"
                            hint="Soyez réaliste. On adapte le programme."
                            options={DAILY_MINUTES.map((m) => ({ value: m, label: `${m} min` }))}
                            value={profile.daily_minutes}
                            onChange={(v) => setProfile((p) => ({ ...p, daily_minutes: v }))}
                            onNext={() => goNext({ daily_minutes: profile.daily_minutes })}
                            onBack={goBack}
                            testId="daily-minutes"
                        />
                    )}
                    {step === 7 && (
                        <StepChoicePills
                            title="Intensité — mots visés par jour"
                            hint="Vous pouvez ajuster à tout moment."
                            options={INTENSITY_WORDS.map((w) => ({
                                value: w,
                                label: `${w} mots`,
                                sub: w <= 25 ? "confortable" : w <= 50 ? "soutenu" : "intensif",
                            }))}
                            value={profile.intensity_words}
                            onChange={(v) => setProfile((p) => ({ ...p, intensity_words: v }))}
                            onNext={() => goNext({ intensity_words: profile.intensity_words })}
                            onBack={goBack}
                            testId="intensity"
                        />
                    )}
                    {step === 8 && (
                        <StepInterests
                            value={profile.interests}
                            onChange={(v) => setProfile((p) => ({ ...p, interests: v }))}
                            onNext={() => goNext({ interests: profile.interests })}
                            onBack={goBack}
                        />
                    )}
                    {step === 9 && (
                        <StepChoicePills
                            title="Modalité préférée"
                            hint="Multimodal reste par défaut le plus efficace."
                            options={MODALITIES.map((m) => ({ value: m.code, label: m.label }))}
                            value={profile.modality}
                            onChange={(v) => setProfile((p) => ({ ...p, modality: v }))}
                            onNext={() => goNext({ modality: profile.modality })}
                            onBack={goBack}
                            testId="modality"
                        />
                    )}
                    {step === 10 && (
                        <StepChoicePills
                            title="Mode de correction"
                            hint="À quel point voulez-vous être repris ?"
                            options={CORRECTIONS.map((m) => ({ value: m.code, label: m.label, sub: m.detail }))}
                            value={profile.correction}
                            onChange={(v) => setProfile((p) => ({ ...p, correction: v }))}
                            onNext={() => goNext({ correction: profile.correction })}
                            onBack={goBack}
                            testId="correction"
                        />
                    )}
                    {step === 11 && (
                        <StepChoicePills
                            title="Durée du programme"
                            hint="Vous pourrez enchaîner un nouveau plan à la fin."
                            options={PROGRAM_LENGTHS.map((p) => ({ value: p.days, label: p.label }))}
                            value={profile.program_length_days}
                            onChange={(v) => setProfile((p) => ({ ...p, program_length_days: v }))}
                            hideNext
                            testId="program-length"
                        >
                            <div className="mt-8 flex flex-wrap gap-3 justify-between">
                                <button
                                    type="button"
                                    onClick={goBack}
                                    className="text-sm text-inkSoft hover:text-ink inline-flex items-center gap-1"
                                    data-testid="wiz-back"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Retour
                                </button>
                                <button
                                    onClick={generateProgram}
                                    disabled={generating}
                                    data-testid="generate-program-btn"
                                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-3 text-sm font-medium active:scale-[0.98] transition-transform disabled:opacity-60"
                                >
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    {generating ? "Génération de votre programme…" : "Générer mon programme"}
                                </button>
                            </div>
                        </StepChoicePills>
                    )}

                    {saving && (
                        <p className="mt-6 text-xs text-inkSoft flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Sauvegarde…
                        </p>
                    )}
                </div>

                <div className="mt-6 text-center">
                    <Link to="/" className="text-xs text-inkSoft hover:text-ink" data-testid="quit-onboarding">
                        Quitter — je reprendrai plus tard
                    </Link>
                </div>
            </div>
        </div>
    );
}

/* ---------- individual steps ---------- */

function StepHeader({ title, hint }) {
    return (
        <div className="mb-6">
            <h2 className="font-serif text-2xl md:text-3xl tracking-tight" data-testid="step-title">
                {title}
            </h2>
            {hint && <p className="mt-2 text-sm text-inkSoft leading-relaxed">{hint}</p>}
        </div>
    );
}

function StepPrenom({ value, onNext }) {
    const [v, setV] = useState(value || "");
    return (
        <>
            <StepHeader title="Comment vous appelle-t-on ?" hint="Un prénom, un pseudo, à vous de voir." />
            <input
                autoFocus
                value={v}
                onChange={(e) => setV(e.target.value)}
                placeholder="Camille"
                data-testid="input-first-name"
                className="w-full h-12 rounded-xl border border-lexiBorder bg-paper px-4 text-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
            <div className="mt-8 flex justify-end">
                <button
                    onClick={() => v.trim() && onNext(v.trim())}
                    disabled={!v.trim()}
                    data-testid="wiz-next"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
                >
                    Continuer <ArrowRight className="w-4 h-4" />
                </button>
            </div>
        </>
    );
}

function StepLangueSelector({ title, hint, value, onChange, onNext, onBack, testId, excludeIso }) {
    return (
        <>
            <StepHeader title={title} hint={hint} />
            <LanguagePicker
                value={value}
                onChange={(l) => onChange(l.iso)}
                testIdPrefix={testId}
                excludeIsos={excludeIso ? [excludeIso] : []}
            />
            <NavButtons onBack={onBack} onNext={onNext} disableNext={!value} />
        </>
    );
}

function StepVariant({ targetLang, variants, value, onChange, onNext, onBack }) {
    return (
        <>
            <StepHeader
                title={`Quelle variante de ${targetLang?.fr} ?`}
                hint="Le choix influe sur l'accent, le vocabulaire et les tournures."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {variants.map((l) => {
                    const active = l.iso === value;
                    return (
                        <button
                            key={l.iso}
                            type="button"
                            onClick={() => onChange(l)}
                            data-testid={`variant-${l.iso}`}
                            className={`text-left p-4 rounded-2xl border transition-colors ${
                                active
                                    ? "bg-emerald text-white border-emerald"
                                    : "bg-paper border-lexiBorder hover:bg-paperAlt"
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{l.flag}</span>
                                <div>
                                    <div className="font-serif text-lg leading-none">{l.native}</div>
                                    <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-inkSoft"}`}>
                                        {l.variant}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3">
                                <QualityBadge quality={l.tts} inverted={active} />
                            </div>
                        </button>
                    );
                })}
            </div>
            <NavButtons onBack={onBack} onNext={onNext} disableNext={!value} />
        </>
    );
}

function StepNiveau({ value, onChange, onNext, onBack, onTakeTest, targetLang }) {
    return (
        <>
            <StepHeader
                title={`Où en êtes-vous en ${targetLang?.fr || "cette langue"} ?`}
                hint="Si vous ne savez pas, prenez le test — c'est court et adaptatif."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {LEVELS.map((l) => {
                    const active = l.code === value;
                    return (
                        <button
                            key={l.code}
                            type="button"
                            onClick={() => onChange(l.code)}
                            data-testid={`level-${l.code}`}
                            className={`text-left p-4 rounded-xl border transition-colors ${
                                active ? "bg-ink text-white border-ink" : "bg-paper border-lexiBorder hover:bg-paperAlt"
                            }`}
                        >
                            <div className="font-mono text-sm">{l.code}</div>
                            <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-inkSoft"}`}>
                                {l.label.split(" — ")[1] || l.label}
                            </div>
                        </button>
                    );
                })}
            </div>
            <div className="mt-8 flex flex-wrap gap-3 justify-between">
                <button
                    type="button"
                    onClick={onBack}
                    className="text-sm text-inkSoft hover:text-ink inline-flex items-center gap-1"
                    data-testid="wiz-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <div className="flex flex-wrap gap-3">
                    {value === "inconnu" && (
                        <button
                            type="button"
                            onClick={onTakeTest}
                            data-testid="take-placement-btn"
                            className="inline-flex items-center gap-2 bg-emerald hover:bg-emeraldSoft text-white rounded-full px-5 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                        >
                            <Sparkles className="w-4 h-4" />
                            Faire le test adaptatif
                        </button>
                    )}
                    <button
                        onClick={onNext}
                        data-testid="wiz-next"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-2.5 text-sm font-medium active:scale-[0.98] transition-transform"
                    >
                        Continuer <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </>
    );
}

function StepObjectif({ goal, goalDetail, setGoal, setDetail, onNext, onBack }) {
    return (
        <>
            <StepHeader title="Quel est votre objectif principal ?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {GOALS.map((g) => {
                    const active = g.code === goal;
                    return (
                        <button
                            key={g.code}
                            type="button"
                            onClick={() => setGoal(g.code)}
                            data-testid={`goal-${g.code}`}
                            className={`text-left p-4 rounded-xl border transition-colors ${
                                active ? "bg-ink text-white border-ink" : "bg-paper border-lexiBorder hover:bg-paperAlt"
                            }`}
                        >
                            <div className="font-serif text-base leading-none">{g.label}</div>
                            <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-inkSoft"}`}>{g.detail}</div>
                        </button>
                    );
                })}
            </div>
            {(goal === "autre" || goal === "médical" || goal === "examen") && (
                <input
                    value={goalDetail || ""}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder={
                        goal === "médical"
                            ? "Ex. urgences, gériatrie, télémédecine…"
                            : goal === "examen"
                              ? "Ex. TOEFL en juin, DELE B2…"
                              : "Précisez votre objectif"
                    }
                    data-testid="goal-detail"
                    className="mt-4 w-full h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
            )}
            <NavButtons onBack={onBack} onNext={onNext} />
        </>
    );
}

function StepInterests({ value, onChange, onNext, onBack }) {
    const [custom, setCustom] = useState("");
    const toggle = (tag) => {
        if (value.includes(tag)) onChange(value.filter((t) => t !== tag));
        else onChange([...value, tag]);
    };
    const addCustom = () => {
        const t = custom.trim();
        if (t && !value.includes(t)) onChange([...value, t]);
        setCustom("");
    };
    return (
        <>
            <StepHeader
                title="Vos centres d'intérêt"
                hint="Le vocabulaire et les scénarios s'orientent vers ce que vous aimez."
            />
            <div className="flex flex-wrap gap-2" data-testid="interests-suggestions">
                {INTEREST_SUGGESTIONS.map((tag) => {
                    const active = value.includes(tag);
                    return (
                        <button
                            key={tag}
                            type="button"
                            onClick={() => toggle(tag)}
                            data-testid={`interest-${tag}`}
                            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                active ? "bg-emerald text-white border-emerald" : "bg-paper border-lexiBorder hover:bg-paperAlt"
                            }`}
                        >
                            {tag}
                        </button>
                    );
                })}
            </div>
            <div className="mt-5 flex gap-2">
                <input
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            addCustom();
                        }
                    }}
                    placeholder="Ajouter un autre thème…"
                    data-testid="interest-input"
                    className="flex-1 h-11 rounded-xl border border-lexiBorder bg-paper px-4 focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
                <button
                    type="button"
                    onClick={addCustom}
                    disabled={!custom.trim()}
                    data-testid="interest-add"
                    className="px-4 h-11 rounded-xl border border-lexiBorder bg-white hover:bg-paperAlt text-sm disabled:opacity-50"
                >
                    Ajouter
                </button>
            </div>
            {value.filter((t) => !INTEREST_SUGGESTIONS.includes(t)).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                    {value
                        .filter((t) => !INTEREST_SUGGESTIONS.includes(t))
                        .map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => toggle(t)}
                                data-testid={`interest-custom-${t}`}
                                className="px-3 py-1.5 rounded-full text-sm border bg-emerald text-white border-emerald"
                            >
                                {t} ×
                            </button>
                        ))}
                </div>
            )}
            <NavButtons onBack={onBack} onNext={onNext} />
        </>
    );
}

function StepChoicePills({ title, hint, options, value, onChange, onNext, onBack, testId, hideNext, children }) {
    return (
        <>
            <StepHeader title={title} hint={hint} />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`${testId}-picker`}>
                {options.map((o) => {
                    const active = o.value === value;
                    return (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => onChange(o.value)}
                            data-testid={`${testId}-${o.value}`}
                            className={`p-4 rounded-xl border text-left transition-colors ${
                                active ? "bg-ink text-white border-ink" : "bg-paper border-lexiBorder hover:bg-paperAlt"
                            }`}
                        >
                            <div className="font-serif text-lg leading-none">{o.label}</div>
                            {o.sub && (
                                <div className={`text-xs mt-1 ${active ? "text-white/80" : "text-inkSoft"}`}>
                                    {o.sub}
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
            {children}
            {!hideNext && <NavButtons onBack={onBack} onNext={onNext} />}
        </>
    );
}

function NavButtons({ onBack, onNext, disableNext }) {
    return (
        <div className="mt-8 flex justify-between items-center">
            <button
                type="button"
                onClick={onBack}
                className="text-sm text-inkSoft hover:text-ink inline-flex items-center gap-1"
                data-testid="wiz-back"
            >
                <ArrowLeft className="w-4 h-4" /> Retour
            </button>
            <button
                type="button"
                onClick={onNext}
                disabled={disableNext}
                data-testid="wiz-next"
                className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60 active:scale-[0.98] transition-transform"
            >
                Continuer <ArrowRight className="w-4 h-4" />
            </button>
        </div>
    );
}
